import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { verifyAgentToken, getAgentSessions, getAgentChatHistory, claimAgentSession, sendAgentMessage, API_BASE } from '../api/client';
import { Send, CheckCircle, Clock, Search, AlertCircle, RefreshCw, Hand, Shield, Phone, Video } from 'lucide-react';
import WebRTCCallOverlay from '../components/WebRTCCallOverlay';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';

const AgentPortalPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [agent, setAgent] = useState(null);
    const [verifying, setVerifying] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [connected, setConnected] = useState(false);

    // Call state
    const [callStatus, setCallStatus] = useState('idle');
    const [callType, setCallType] = useState('video');
    const [remoteName, setRemoteName] = useState('Customer');

    const stompClientRef = useRef(null);
    const [, setForceRender] = useState({});
    const messagesEndRef = useRef(null);

    useEffect(() => {
        verify();
    }, [token]);

    const verify = async () => {
        try {
            const agentInfo = await verifyAgentToken(token);
            setAgent(agentInfo);
            fetchSessions();
            initWebSocket();
        } catch (err) {
            console.error(err);
            setAgent(null);
        } finally {
            setVerifying(false);
        }
    };

    const fetchSessions = async () => {
        try {
            const result = await getAgentSessions(token);
            setSessions(result || []);
        } catch (e) {
            console.warn(e);
        }
    };

    const wsRetryCountRef = useRef(0);
    const MAX_WS_RETRIES = 5;

    const initWebSocket = () => {
        if (wsRetryCountRef.current >= MAX_WS_RETRIES) {
            console.warn('WebSocket: Max retries reached, stopping reconnect');
            return;
        }

        const socket = new SockJS(`${API_BASE}/ws/chat`);
        const stompClient = Stomp.over(socket);
        stompClient.debug = () => { }; // Disable debug

        stompClient.connect({ Authorization: `Bearer ${token}` }, () => {
            wsRetryCountRef.current = 0; // Reset on successful connect
            setConnected(true);
            stompClient.subscribe('/topic/admin/support', (messageOutput) => {
                fetchSessions();
            });
        }, (error) => {
            console.warn('STOMP Error', error);
            setConnected(false);
            wsRetryCountRef.current += 1;
            if (wsRetryCountRef.current < MAX_WS_RETRIES) {
                setTimeout(initWebSocket, 5000 * wsRetryCountRef.current); // Exponential backoff
            } else {
                console.error('WebSocket: Max retries exceeded, giving up');
            }
        });

        stompClientRef.current = stompClient;
        setForceRender({});
    };

    useEffect(() => {
        if (connected && stompClientRef.current) {
            // Subscribe to ALL sessions for incoming WebRTC calls
            const subs = sessions.map(session => {
                return stompClientRef.current.subscribe(`/topic/chat/${session.id}/signal`, (msg) => {
                    const signal = JSON.parse(msg.body);
                    window.dispatchEvent(new CustomEvent('webrtc-signal', { detail: signal }));
                });
            });

            return () => {
                subs.forEach(sub => sub.unsubscribe());
            };
        }
    }, [sessions, connected]);

    useEffect(() => {
        if (activeSession) {
            fetchChatHistory(activeSession.id);
            if (stompClientRef.current && connected) {
                const sub = stompClientRef.current.subscribe(`/topic/chat/${activeSession.id}`, (msg) => {
                    const parsed = JSON.parse(msg.body);
                    setMessages(prev => {
                        if (prev.find(m => m.id === parsed.id)) return prev;
                        return [...prev, parsed];
                    });
                    scrollToBottom();
                });

                return () => { sub.unsubscribe(); };
            }
        }
    }, [activeSession, connected]);

    const fetchChatHistory = async (sessionId) => {
        try {
            const hist = await getAgentChatHistory(sessionId, token);
            setMessages(hist || []);
            scrollToBottom();
        } catch (e) {
            console.warn(e);
        }
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleClaimSession = async (session) => {
        try {
            const updated = await claimAgentSession(session.id, token);
            setActiveSession(updated);
            fetchSessions();
        } catch (e) {
            console.error('Failed to claim session', e);
            alert('Failed to claim session. Someone else may have taken it.');
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputText.trim() || !activeSession) return;
        const msgText = inputText;
        setInputText('');

        try {
            await sendAgentMessage(token, {
                sessionId: activeSession.id,
                message: msgText,
                userId: activeSession.customerId,
            });
        } catch (err) {
            console.error('Failed to send message:', err);
            alert('Failed to send message. Please try again.');
        }
    };

    if (verifying) {
        return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
    }

    if (!agent) {
        return (
            <div style={{ display: 'flex', height: '100vh', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)' }}>
                <Shield size={64} color="var(--danger)" style={{ marginBottom: 16 }} />
                <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-main)' }}>Link Invalid or Revoked</h2>
                <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>This magic link is no longer active. Please contact your administrator.</p>
            </div>
        );
    }

    const unassignedCount = sessions.filter(s => !s.adminId).length;
    const mySessionsCount = sessions.filter(s => s.adminId && String(s.adminId).endsWith(String(agent.id))).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-alt, #f3f4f6)', color: 'var(--text-main, #111827)', overflow: 'hidden' }}>
            <div style={{ padding: '16px', background: 'var(--card-bg, #ffffff)', borderBottom: '1px solid var(--border, #e5e7eb)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--primary, #2563eb)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                        {agent.name.charAt(0)}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-main, #111827)', fontSize: 16 }}>{agent.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: connected ? 'var(--success, #10b981)' : 'var(--danger, #ef4444)' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? 'var(--success, #10b981)' : 'var(--danger, #ef4444)' }}></div>
                            {connected ? 'System Connected' : 'Reconnecting...'}
                        </div>
                    </div>
                </div>
                {activeSession && (
                    <button onClick={() => setActiveSession(null)} style={{ background: 'transparent', border: '1px solid var(--border, #e5e7eb)', borderRadius: 6, padding: '6px 12px', color: 'var(--text-main, #111827)' }}>
                        Back to Queue
                    </button>
                )}
            </div>

            {!activeSession ? (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Support Queue <span style={{ background: 'var(--primary, #2563eb)', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: 12, marginLeft: 8 }}>{sessions.length}</span></h3>
                        <button onClick={fetchSessions} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted, #6b7280)', cursor: 'pointer' }}><RefreshCw size={18} /></button>
                    </div>

                    {sessions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted, #6b7280)' }}>
                            <CheckCircle size={48} color="var(--success, #10b981)" style={{ opacity: 0.5, margin: '0 auto 16px' }} />
                            <p>All active chats are currently being handled! You are caught up.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {sessions.map(s => {
                                const isUnassigned = !s.adminId;
                                const isMine = String(s.adminId).endsWith(String(agent.id));

                                return (
                                    <div key={s.id} onClick={() => isMine ? setActiveSession(s) : null} style={{
                                        background: 'var(--card-bg, #ffffff)',
                                        border: `1px solid ${isUnassigned ? 'var(--primary, #2563eb)' : 'var(--border, #e5e7eb)'}`,
                                        borderRadius: 8,
                                        padding: 16,
                                        cursor: isMine ? 'pointer' : 'default',
                                        boxShadow: isUnassigned ? '0 4px 12px rgba(37, 99, 235, 0.1)' : 'none'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600, color: 'var(--text-main, #111827)' }}>Customer #{s.customerId}</span>
                                            <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
                                                {new Date(s.lastActive).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                                            {s.needsAdminReview && <span style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger, #ef4444)', fontSize: 11, padding: '4px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}><AlertCircle size={12} /> AI Escalate</span>}

                                            {isUnassigned ? (
                                                <button onClick={(e) => { e.stopPropagation(); handleClaimSession(s); }} style={{ background: 'var(--primary, #2563eb)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', cursor: 'pointer' }}>
                                                    <Hand size={14} /> Claim Session
                                                </button>
                                            ) : isMine ? (
                                                <span style={{ fontSize: 13, color: 'var(--primary, #2563eb)', fontWeight: 600 }}>In Progress - Open</span>
                                            ) : (
                                                <span style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>Handled by another Agent</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-base)' }}>
                    <div style={{ background: 'var(--bg-alt, #f3f4f6)', padding: '12px 16px', borderBottom: '1px solid var(--border, #e5e7eb)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-main, #111827)' }}>Assisting Customer #{activeSession.customerId}</span>
                            {activeSession.needsAdminReview && <span style={{ color: 'var(--danger, #ef4444)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><AlertCircle size={14} /> Escalated</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={() => window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', { detail: { type: 'audio' }}))} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}>
                                <Phone size={18} color="var(--primary, #2563eb)" />
                            </button>
                            <button onClick={() => window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', { detail: { type: 'video' }}))} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}>
                                <Video size={18} color="var(--primary, #2563eb)" />
                            </button>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {messages.length === 0 ? (
                            <div style={{ textAlign: 'center', color: 'var(--text-muted, #6b7280)', margin: 'auto 0' }}>No messages yet. Say hello!</div>
                        ) : (
                            messages.map(msg => {
                                const isSelf = typeof msg.senderId === 'number' && String(msg.senderId).endsWith(String(agent.id));
                                const isAI = msg.senderId === 0;
                                const isSystem = msg.senderType === 'system';

                                if (isSystem) {
                                    return <div key={msg.id} style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', margin: '8px 0' }}>{msg.content}</div>;
                                }

                                return (
                                    <div key={msg.id} style={{ alignSelf: isSelf ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                                        <div style={{
                                            background: isSelf ? 'var(--primary, #2563eb)' : isAI ? 'var(--bg-alt, #f3f4f6)' : 'var(--card-bg, #ffffff)',
                                            color: isSelf ? '#ffffff' : 'var(--text-main, #111827)',
                                            padding: '10px 14px',
                                            borderRadius: isSelf ? '16px 16px 0 16px' : '16px 16px 16px 0',
                                            border: !isSelf ? '1px solid var(--border, #e5e7eb)' : 'none',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                        }}>
                                            {isAI && <span style={{ fontSize: 11, color: 'var(--primary, #2563eb)', fontWeight: 600, display: 'block', marginBottom: 4 }}>AI Assistant</span>}
                                            <span style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontSize: 14 }}>{msg.content.replace(/^.*?:: /, '')}</span>
                                        </div>
                                        <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginTop: 4, display: 'block', textAlign: isSelf ? 'right' : 'left' }}>
                                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form onSubmit={handleSendMessage} style={{ padding: '12px 16px', background: 'var(--card-bg, #ffffff)', borderTop: '1px solid var(--border, #e5e7eb)', display: 'flex', gap: 8 }}>
                        <input
                            type="text"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder="Type a message..."
                            style={{ flex: 1, padding: '12px 16px', background: 'var(--bg-alt, #f3f4f6)', border: '1px solid var(--border, #e5e7eb)', borderRadius: 24, outline: 'none', fontSize: 15, color: 'var(--text-main, #111827)' }}
                        />
                        <button type="submit" disabled={!inputText.trim() || !connected} style={{ width: 44, height: 44, borderRadius: '50%', background: inputText.trim() && connected ? 'var(--primary, #2563eb)' : 'var(--border, #e5e7eb)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: inputText.trim() && connected ? 'pointer' : 'not-allowed', transition: '0.2s' }}>
                            <Send size={18} />
                        </button>
                    </form>
                </div>
            )}

            <WebRTCCallOverlay
                sessionId={activeSession?.id}
                customerId={agent ? 1000000 + agent.id : 0}
                stompClient={stompClientRef.current}
                currentStatus={callStatus}
                setCurrentStatus={setCallStatus}
                callType={callType}
                setCallType={setCallType}
                remoteName={remoteName}
                setRemoteName={setRemoteName}
            />
        </div>
    );
};

export default AgentPortalPage;
